import { Button } from "@/components/ui/button";
import { ArrowRight, ChevronDown } from "lucide-react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getBrandConfig } from "@/config/brand";

export const HeroSection = () => {
  const { t } = useTranslation();
  const { hero } = getBrandConfig();
  const scrollToFeatures = () => {
    document.getElementById("meeting-analysis")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section className="relative min-h-screen flex flex-col items-center justify-center overflow-hidden bg-background">
      {/* Background Grid */}
      <div className="absolute inset-0 bg-grid-pattern opacity-[0.03] dark:opacity-[0.05]" />

      {/* Ambient Glow */}
      <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[800px] h-[800px] bg-primary/10 blur-[150px] rounded-full pointer-events-none" />
      <div className="absolute bottom-0 right-0 w-[400px] h-[400px] bg-accent/10 blur-[100px] rounded-full pointer-events-none" />

      {/* Main Content - Centered */}
      <div className="relative z-10 text-center px-4 max-w-4xl mx-auto space-y-8">
        {/* Badge */}
        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 text-primary text-sm font-medium">
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75"></span>
            <span className="relative inline-flex rounded-full h-2 w-2 bg-primary"></span>
          </span>
          {t("landing.hero.badge")}
        </div>

        {/* Main Title - Apple Style */}
        <h1 className="text-5xl sm:text-7xl lg:text-8xl font-bold tracking-tight text-foreground leading-[1.05]">
          <span className="text-gradient">{hero.emphasis}</span>{" "}
          <span className="text-foreground/90">{hero.suffix}</span>
        </h1>

        {/* Tagline */}
        <p className="text-xl sm:text-2xl text-muted-foreground max-w-2xl mx-auto leading-relaxed font-light">
          {t("landing.hero.tagline")}
          <br className="hidden sm:block" />
          <span className="text-foreground/80">{t("landing.hero.taglineHighlight")}</span>
        </p>

        {/* CTAs */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-4">
          <Button
            size="lg"
            className="h-14 px-10 text-lg shadow-xl shadow-primary/20 hover:shadow-primary/40 transition-all rounded-full"
            asChild
          >
            <Link to="/meetings">
              {t("landing.hero.getStarted")} <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
          </Button>
          <Button
            size="lg"
            variant="outline"
            className="h-14 px-10 text-lg border-border hover:bg-muted/50 rounded-full"
            onClick={scrollToFeatures}
          >
            {t("landing.hero.learnMore")}
          </Button>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
        <button
          onClick={scrollToFeatures}
          className="p-2 rounded-full text-muted-foreground hover:text-foreground transition-colors"
          aria-label={t("landing.hero.scrollToFeatures")}
        >
          <ChevronDown className="h-6 w-6" />
        </button>
      </div>
    </section>
  );
};
